import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { PiWarningCircleLight } from 'react-icons/pi';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import CurrencyPicker from '../../shared/TailwindComponents/CurrencyPicker';
import CurrencyInput from '../../shared/TailwindComponents/CurrencyInput';
import type { MatterOutcome, OutcomeTaxonomyEntry, OutcomeWritePayload } from '../../../types/economics';

/** Outcome types that require an amount (a settlement figure) before saving. */
export const AMOUNT_REQUIRED_OUTCOMES = ['settled', 'partial_settlement'];

interface OutcomeModalProps {
  /** 'close' captures the outcome AND closes the matter; 'edit' upserts only. */
  mode: 'close' | 'edit';
  /** Effective outcome taxonomy (org overrides + global defaults), active only. */
  taxonomy: OutcomeTaxonomyEntry[];
  /** Existing outcome to pre-populate (edit mode, or re-close). */
  outcome?: MatterOutcome | null;
  /** Matter currency / org default, used to seed the amount currency. */
  defaultCurrency: string;
  onSave: (payload: OutcomeWritePayload) => Promise<void>;
  onAfterSave?: () => Promise<void> | void;
  /** Reports whether the form is complete so the host can enable/disable Save. */
  onValidityChange?: (valid: boolean) => void;
}

export interface OutcomeModalHandle {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

const today = (): string => new Date().toISOString().split('T')[0];

/**
 * Capture / edit a matter outcome (US #2240 / #2241). Rendered via GlobalModal
 * ('matterOutcome'). In 'close' mode the host calls POST /matters/<uuid>/close;
 * in 'edit' mode PUT /matters/<uuid>/outcome (firm-admin) with an audit warning.
 */
const OutcomeModal = forwardRef<OutcomeModalHandle, OutcomeModalProps>(
  ({ mode, taxonomy, outcome, defaultCurrency, onSave, onAfterSave, onValidityChange }, ref) => {
    const { t } = useTranslation();

    const [outcomeTypeId, setOutcomeTypeId] = useState<string>(
      outcome?.outcome_type_id ? String(outcome.outcome_type_id) : '',
    );
    const [amount, setAmount] = useState<string>(
      outcome?.outcome_amount != null ? String(outcome.outcome_amount) : '',
    );
    const [currency, setCurrency] = useState<string>(outcome?.currency || defaultCurrency);
    const [outcomeDate, setOutcomeDate] = useState<string>(outcome?.outcome_date || today());
    const [notes, setNotes] = useState<string>(outcome?.notes || '');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ type?: string; date?: string; amount?: string }>({});

    const options = useMemo(
      () => taxonomy.map((entry) => ({ value: String(entry.id), label: entry.label })),
      [taxonomy],
    );

    // Amount is mandatory only for settlement-type outcomes.
    const selectedName = useMemo(
      () => taxonomy.find((e) => String(e.id) === outcomeTypeId)?.name,
      [taxonomy, outcomeTypeId],
    );
    const amountRequired = !!selectedName && AMOUNT_REQUIRED_OUTCOMES.includes(selectedName);

    const isValid = !!outcomeTypeId && !!outcomeDate && (!amountRequired || amount.trim() !== '');
    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const validate = (): boolean => {
      const next: { type?: string; date?: string; amount?: string } = {};
      if (!outcomeTypeId) next.type = 'Select an outcome';
      if (!outcomeDate) next.date = 'Outcome date is required';
      if (amountRequired && amount.trim() === '') next.amount = 'Amount is required for a settlement';
      else if (amount.trim() !== '' && Number.isNaN(Number(amount))) next.amount = 'Enter a valid amount';
      setErrors(next);
      return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
      if (!validate()) {
        // Throw so GlobalModal keeps the dialog open and the errors stay visible.
        throw new Error('validation');
      }
      setIsSaving(true);
      try {
        await onSave({
          outcome_type_id: Number(outcomeTypeId),
          outcome_date: outcomeDate,
          outcome_amount: amount.trim() === '' ? null : Number(amount),
          currency: currency || null,
          outcome_notes: notes.trim() || null,
        });
        if (onAfterSave) await onAfterSave();
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ handleSave, isSaving }));

    return (
      <div className="px-6 space-y-5 w-[34rem] max-w-[90vw]">
        {mode === 'edit' && (
          <div
            data-testid="outcome-audit-banner"
            className="flex items-start gap-2 p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800"
          >
            <PiWarningCircleLight className="h-5 w-5 text-warning-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-warning-700 dark:text-warning-300">
              {t('This change will be audit-logged.')}
            </p>
          </div>
        )}

        <Dropdown
          id="outcome-type"
          label={t('Outcome')}
          options={options}
          value={outcomeTypeId}
          placeholder={t('Select an outcome')}
          onChange={(v) => setOutcomeTypeId(Array.isArray(v) ? v[0] : v)}
          error={errors.type ? t(errors.type) : undefined}
          required
          fullWidth
          className="w-full"
        />

        <div className="grid grid-cols-2 gap-4">
          <CurrencyInput
            id="outcome-amount"
            label={t('Outcome Amount')}
            value={amount}
            currency={currency}
            onChange={setAmount}
            error={errors.amount ? t(errors.amount) : undefined}
            required={amountRequired}
          />
          <CurrencyPicker
            id="outcome-currency"
            label={t('Currency')}
            value={currency}
            onChange={setCurrency}
          />
        </div>

        <Input
          id="outcome-date"
          type="date"
          label={t('Outcome Date')}
          value={outcomeDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOutcomeDate(e.target.value)}
          error={errors.date ? t(errors.date) : undefined}
          required
          className="w-full"
        />

        <Textarea
          id="outcome-notes"
          label={t('Notes')}
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          placeholder={t('Optional note about the outcome')}
          rows={3}
        />
      </div>
    );
  },
);

OutcomeModal.displayName = 'OutcomeModal';

export default OutcomeModal;
